/**
 * Claims Context
 *
 * Provides user claims state to the entire application.
 * Supports strongly typed claim checks with autocomplete.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/auth";
import { UserClaim, ClaimName, ToggleClaimResult } from "@/types/claims";
import { fetchUserClaims, toggleClaimByCode } from "@/services/claimsService";

interface ClaimsContextType {
  /** All claims the user has (active and inactive) */
  claims: UserClaim[];
  /** Only active claims */
  activeClaims: UserClaim[];
  /** Whether claims are currently loading */
  isLoading: boolean;
  /** Check if user has an active claim by name (strongly typed) */
  hasClaim: (name: ClaimName) => boolean;
  /** Toggle a claim by code */
  toggleClaim: (code: string) => Promise<ToggleClaimResult | null>;
  /** Refresh claims from database */
  refreshClaims: () => Promise<void>;
  /** Get a specific claim by name */
  getClaim: (name: ClaimName) => UserClaim | undefined;
}

const ClaimsContext = createContext<ClaimsContextType | undefined>(undefined);

interface ClaimsProviderProps {
  children: React.ReactNode;
}

export const ClaimsProvider: React.FC<ClaimsProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [claims, setClaims] = useState<UserClaim[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch claims when user changes
  const loadClaims = useCallback(async () => {
    if (!user) {
      setClaims([]);
      return;
    }

    setIsLoading(true);
    try {
      const userClaims = await fetchUserClaims();
      setClaims(userClaims);
    } catch (error) {
      console.error("Failed to load claims:", error);
      setClaims([]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Load claims on mount and when user changes
  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  // Memoized active claims
  const activeClaims = useMemo(() => {
    return claims.filter((c) => c.isActive);
  }, [claims]);

  // Check if user has an active claim (strongly typed)
  const hasClaim = useCallback(
    (name: ClaimName): boolean => {
      return activeClaims.some((c) => c.claimName === name);
    },
    [activeClaims]
  );

  // Get a specific claim by name
  const getClaim = useCallback(
    (name: ClaimName): UserClaim | undefined => {
      return claims.find((c) => c.claimName === name);
    },
    [claims]
  );

  // Toggle a claim by code
  const toggleClaim = useCallback(
    async (code: string): Promise<ToggleClaimResult | null> => {
      if (!user) {
        throw new Error("Must be logged in to toggle claims");
      }

      const result = await toggleClaimByCode(code);

      if (result) {
        // Refresh claims to get updated state
        await loadClaims();
      }

      return result;
    },
    [user, loadClaims]
  );

  // Refresh claims
  const refreshClaims = useCallback(async () => {
    await loadClaims();
  }, [loadClaims]);

  const value = useMemo<ClaimsContextType>(
    () => ({
      claims,
      activeClaims,
      isLoading,
      hasClaim,
      toggleClaim,
      refreshClaims,
      getClaim,
    }),
    [claims, activeClaims, isLoading, hasClaim, toggleClaim, refreshClaims, getClaim]
  );

  return <ClaimsContext.Provider value={value}>{children}</ClaimsContext.Provider>;
};

/**
 * Hook to access claims context
 *
 * @example
 * ```tsx
 * const { hasClaim } = useClaims();
 *
 * if (hasClaim(CLAIM_NAMES.DEBUG)) {
 *   // Show debug tools
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useClaims = (): ClaimsContextType => {
  const context = useContext(ClaimsContext);
  if (context === undefined) {
    throw new Error("useClaims must be used within a ClaimsProvider");
  }
  return context;
};

export default ClaimsContext;
